import { Icon, QuestionOctagonFill, StoplightsFill } from "react-bootstrap-icons";
import { MosaicTrafficLightsValue } from "./MosaicTrafficLightsValue";

/**
 * Traffic light properties.
 *
 * @param icon - The icon component to display for this traffic light
 * @param displayLabel - The text label describing the traffic light status
 * @param cssColor - The CSS colour value for the traffic light
 * @param priority - Optional. The priority value for traffic light ordering
 */
interface IMosaicTrafficLight {
  icon: Icon | null;
  displayLabel: string;
  cssColor: string;
  priority?: number;
}

/**
 * Traffic light parameters for various traffic light states.
 */
export const MosaicTrafficLightColors: {
  [key in MosaicTrafficLightsValue]: IMosaicTrafficLight;
} = {
  none: {
    displayLabel: "",
    cssColor: "",
    icon: null,
  },
  FromScratch: {
    displayLabel: "Created without using an existing template or basic layout",
    cssColor: "#3C1053",
    icon: StoplightsFill,
  },
  FromBasicLayout: {
    displayLabel: "Created using a basic layout",
    cssColor: "#D0006F",
    icon: StoplightsFill,
  },
  FromTemplate: {
    displayLabel: "Created using an existing template",
    cssColor: "#68D2DF",
    icon: StoplightsFill,
  },
  RedError: {
    displayLabel:
      "The shell includes changes that strongly deviate from the AZSOL template. Please submit a change request in CRYSTAL. In case of any questions, please reach out to the AZSOL team.",
    cssColor: "#FF0000",
    icon: StoplightsFill,
    priority: 90,
  },
  AmberChangeExpected: {
    displayLabel: "A customisation is required, please follow the AZSOL General principles.",
    cssColor: "#FFA500",
    icon: StoplightsFill,
    priority: 80,
  },
  GreenOk: {
    displayLabel: "OK",
    cssColor: "#008000",
    icon: StoplightsFill,
    priority: 50,
  },
  GreenOverride: {
    displayLabel: "Override applied",
    cssColor: "#006400",
    icon: StoplightsFill,
    priority: 60,
  },
  BlankNotEvaluated: {
    displayLabel: "Not evaluated",
    cssColor: "#808080",
    icon: StoplightsFill,
    priority: 10,
  },
  ChangedUnknown: {
    displayLabel: "Changed",
    cssColor: "#FF00FF",
    icon: QuestionOctagonFill,
    priority: 100,
  },
};

/**
 * Class for managing traffic light values and their associated properties.
 */
export class MosaicTrafficLightClass {
  private _value: MosaicTrafficLightsValue = "none";

  /**
   * Gets the current traffic light value.
   */
  get value() {
    return this._value;
  }

  /**
   * Sets the current traffic light value.
   */
  set value(newValue: MosaicTrafficLightsValue) {
    this._value = newValue;
  }

  /**
   * Gets the display label for the current traffic light.
   */
  get displayLabel() {
    return MosaicTrafficLightColors[this._value].displayLabel;
  }

  /**
   * Gets the CSS colour for the current traffic light.
   */
  get cssColor() {
    return MosaicTrafficLightColors[this._value].cssColor;
  }

  private _position = "relative";
  private _top = "-8px";
  private _left = "-16px";

  /**
   * Gets the JSX element for rendering the traffic light icon.
   */
  get element(): React.ReactElement {
    const current = MosaicTrafficLightColors[this._value];
    return current.icon ? (
      <div
        className="traffic-light"
        style={
          this._position === "absolute" ||
          (this._top !== "0px" && this._top !== "0") ||
          (this._left !== "0px" && this._left !== "0")
            ? { position: "relative" }
            : { display: "inline-block" }
        }
        aria-label="Traffic light holder"
      >
        <current.icon
          role={"alert"}
          style={
            this._left !== "0px" && this._left !== "0" && this._top !== "0px" && this._top !== "0"
              ? {
                  position: "absolute",
                  left: `${this._left}`,
                  top: `${this._top}`,
                }
              : undefined
          }
          title={current.displayLabel}
          aria-label={`${this.displayLabel}`}
          color={current.cssColor}
        />
      </div>
    ) : (
      <></>
    );
  }

  /**
   * Creates a new traffic light instance.
   *
   * @param value - The traffic light value or array of values to determine priority
   * @param top - The top position offset for the icon
   * @param left - The left position offset for the icon
   * @param position - The CSS position property for the icon
   */
  public constructor(
    value: MosaicTrafficLightsValue | MosaicTrafficLightsValue[],
    top = "-8px",
    left = "-16px",
    position = "relative",
  ) {
    /* Take highest priority value if array is given */
    if (Array.isArray(value)) {
      this.value = value.reduce((highest, current) => {
        const currentPriority = MosaicTrafficLightColors[current]?.priority ?? 0;
        const highestPriority = MosaicTrafficLightColors[highest]?.priority ?? 0;
        return currentPriority > highestPriority ? current : highest;
      }, value[0]);
    } else {
      this.value = value;
    }
    this._top = top;
    this._left = left;
    this._position = position;
  }
}
